import React from "react";

import { useTranslation } from "utils";

import { Button, Drawer, Icon, Typography } from "components";

export type HowThisWorksDrawerProps = {
  visible: boolean;
  onClose: () => void;
  /** Hands the farmer on to the detailed calculations, opened in edit mode. */
  onSetPricesAndCosts: () => void;
  dataTestId?: string;
};

const STEP_KEYS = ["field", "rotation", "monthly", "price"] as const;
/** Only these steps carry a caveat note in the copy. */
const STEPS_WITH_NOTE: ReadonlySet<(typeof STEP_KEYS)[number]> = new Set(["rotation", "price"]);
const SOURCE_KEYS = ["soil", "weather", "averages"] as const;

export const HowThisWorksDrawer = ({ visible, onClose, onSetPricesAndCosts, dataTestId = "how-this-works-drawer" }: HowThisWorksDrawerProps) => {
  const { t } = useTranslation("scenarioAnalysis");

  return (
    // The hero renders the title; antd's header is hidden in CSS, and `title` still names the mixpanel event.
    <Drawer
      className="soil-sight-how-this-works-drawer"
      visible={visible}
      productEvent="soilsights:explainer_view"
      productEventProperties={{ explainer: "how_this_works" }}
      onClose={onClose}
      title={t("howThisWorks.title")}
      hideCloseIcon
      dataTestId={dataTestId}
    >
      <div className="soil-sight-how-this-works">
        {/* Fixed while the rest scrolls, so the close control stays reachable from anywhere in the copy. */}
        <header className="soil-sight-how-this-works-hero">
          <div className="soil-sight-how-this-works-hero-top">
            <span className="soil-sight-how-this-works-hero-badge">
              <Icon iconName="tabler/calculator" size="m-light" aria-hidden />
            </span>
            <button
              type="button"
              className="soil-sight-how-this-works-close"
              onClick={onClose}
              aria-label={t("global:close")}
              data-testid={`${dataTestId}-close`}
            >
              <Icon iconName="tabler/x" size="m-light" aria-hidden />
            </button>
          </div>
          <Typography variant="paragraph-s" className="soil-sight-how-this-works-eyebrow">
            {t("howThisWorks.eyebrow")}
          </Typography>
          <Typography variant="h3" color="white" className="soil-sight-how-this-works-title">
            {t("howThisWorks.title")}
          </Typography>
          {/* One paragraph in two spans, so the closing sentence sets in bold without leaving the text flow. */}
          <div className="soil-sight-how-this-works-intro">
            <Typography variant="paragraph-m" className="soil-sight-how-this-works-intro-text">
              {t("howThisWorks.intro")}
            </Typography>{" "}
            <Typography variant="paragraph-m" bold color="white">
              {t("howThisWorks.introEmphasis")}
            </Typography>
          </div>
        </header>

        <div className="soil-sight-how-this-works-body">
          <section className="soil-sight-how-this-works-section">
            <Typography variant="paragraph-s" className="soil-sight-how-this-works-section-heading">
              {t("howThisWorks.stepsHeading")}
            </Typography>
            <ol className="soil-sight-how-this-works-steps" data-testid={`${dataTestId}-steps`}>
              {STEP_KEYS.map((key, index) => {
                const note = STEPS_WITH_NOTE.has(key) ? t(`howThisWorks.steps.${key}.note`) : "";
                return (
                  <li key={key} className="soil-sight-how-this-works-step">
                    <span className="soil-sight-how-this-works-step-marker" aria-hidden>
                      {index + 1}
                    </span>
                    <div className="soil-sight-how-this-works-step-copy">
                      <Typography variant="paragraph-m" bold>
                        {t(`howThisWorks.steps.${key}.title`)}
                      </Typography>
                      <Typography variant="paragraph-m" color="grey-600">
                        {t(`howThisWorks.steps.${key}.body`)}
                      </Typography>
                      {note ? (
                        <Typography variant="paragraph-m" className="soil-sight-how-this-works-step-note">
                          {note}
                        </Typography>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ol>
          </section>

          <section className="soil-sight-how-this-works-section">
            <Typography variant="paragraph-s" className="soil-sight-how-this-works-section-heading">
              {t("howThisWorks.sourcesHeading")}
            </Typography>
            <ul className="soil-sight-how-this-works-sources" data-testid={`${dataTestId}-sources`}>
              {SOURCE_KEYS.map((key) => (
                <li key={key} className="soil-sight-how-this-works-source">
                  <Typography variant="paragraph-m" bold>
                    {t(`howThisWorks.sources.${key}.title`)}
                  </Typography>
                  <Typography variant="paragraph-m" color="grey-600">
                    {t(`howThisWorks.sources.${key}.body`)}
                  </Typography>
                </li>
              ))}
            </ul>
          </section>

          <section className="soil-sight-how-this-works-closing">
            <Typography variant="paragraph-m">{t("howThisWorks.closing")}</Typography>
            <Button
              type="primary"
              className="soil-sight-how-this-works-cta"
              onClick={onSetPricesAndCosts}
              mixpanelText="Set your prices and costs"
              dataTestId={`${dataTestId}-cta`}
            >
              {t("howThisWorks.cta")}
            </Button>
          </section>

          <Typography variant="paragraph-s" color="grey-600">
            {t("howThisWorks.fullMethod")}
          </Typography>
        </div>
      </div>
    </Drawer>
  );
};

export default HowThisWorksDrawer;
